import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import { StatusTag } from "../../../components/ui";

// Team members — list, search, add, edit and remove the users inside the
// current entity (tenant), plus assign each a role. Built on the app's vf
// design system so it reads as a sibling of Manage roles (/admin/role).
//
// APIs:
//   POST /common/user/get-filtered   { filterList, sortList, pagingNo, pageSize }
//   GET  /common/base/roles/get-all
//   POST /common/user/save-user-role { id?, firstName, lastName, ... , roleId }
//   POST /common/user/trash          { id }

// Display rename: "EvueMe Support" → "Ample Hire Support" (and any EvueMe → Ample Hire).
const renameRole = (name) => (name || "").replace(/EvueMe/gi, "Ample Hire");

const initials = (fn = "", ln = "") =>
  `${(fn || "").charAt(0)}${(ln || "").charAt(0)}`.toUpperCase() || "?";

const ROLE_TAG = (roleName) => {
  const r = renameRole(roleName).toLowerCase();
  if (r.includes("admin"))     return <StatusTag type="violet" text={renameRole(roleName)} />;
  if (r.includes("recruiter")) return <StatusTag type="indigo" text={renameRole(roleName)} />;
  if (r.includes("support"))   return <StatusTag type="slate" text={renameRole(roleName)} />;
  if (r.match(/l1|l2|l3/))     return <StatusTag type="blue" text={renameRole(roleName)} />;
  return <StatusTag type="slate" text={renameRole(roleName) || "—"} />;
};

const TeamMembers = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formModal, setFormModal] = useState(null); // { member? } — add/edit
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Team members - Ample Hire";
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        axiosInstance
          .post(`${baseUrl}/common/user/get-filtered`, { filterList: [], sortList: [], pagingNo: 1, pageSize: 100 })
          .catch(() => ({ data: { list: [] } })),
        axiosInstance
          .get(`${baseUrl}/common/base/roles/get-all`)
          .catch(() => ({ data: { list: [] } })),
      ]);
      setUsers(u.data?.list || u.data?.data || []);
      setRoles(r.data?.list || r.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter((u) =>
      `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase().includes(q) ||
      (u.primaryEmailId || "").toLowerCase().includes(q) ||
      renameRole(u.roleName || "").toLowerCase().includes(q) ||
      (u.mobileNumber1 || "").includes(q),
    );
  }, [users, search]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => !u.isSuspended && u.accountStatus === 2).length,
    rolesUsed: new Set(users.map((u) => u.roleName).filter(Boolean)).size,
  }), [users]);

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axiosInstance.post(`${baseUrl}/common/user/trash`, { id: confirmDelete.id });
      setConfirmDelete(null);
      toast.success("Team member removed.");
      loadAll();
    } catch (e) {
      toast.error("Failed to remove: " + (e.response?.data?.message || e.message));
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-2 py-2 font-sans page-enter">
      {/* Header */}
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-vf-base font-heading">Team members</h1>
          <p className="text-sm text-vf-muted mt-1">
            {stats.total} member{stats.total !== 1 ? "s" : ""} in your organization
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => toast.info("CSV import is coming soon.")} className="btn-secondary btn-sm">
            Import CSV
          </button>
          <button onClick={() => toast.info("CSV export is coming soon.")} className="btn-secondary btn-sm">
            Export CSV
          </button>
          <button onClick={() => setFormModal({})} className="btn-primary btn-sm">
            + Add member
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatTile label="Total members" value={stats.total} className="text-vf-base" />
        <StatTile label="Active" value={stats.active} className="text-vf-success" />
        <StatTile label="Roles used" value={stats.rolesUsed} className="text-vf-primary" />
      </div>

      {/* Search */}
      <div className="card p-0 overflow-hidden mb-5">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-vf-muted"
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              className="input pl-9 w-72"
              placeholder="Search by name, email, role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="text-xs text-vf-muted">
            {filtered.length} member{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-center">
            <div className="skeleton h-4 w-32 mx-auto rounded" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            {users.length === 0
              ? "No team members yet. Click + Add member to invite one."
              : "No team members match your search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="tbl-header">
                  <th className="px-4 py-3 w-12"></th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">WhatsApp</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3 w-24">Status</th>
                  <th className="px-4 py-3 w-40 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="tbl-row">
                    <td className="px-4 py-3">
                      <div
                        className="w-8 h-8 rounded-full bg-vf-primary text-white flex items-center justify-center font-bold text-[11px]"
                        aria-hidden="true"
                      >
                        {initials(u.firstName, u.lastName)}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-vf-base">
                      {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-vf-muted">{u.primaryEmailId || "—"}</td>
                    <td className="px-4 py-3 text-vf-muted font-mono text-xs">{u.mobileNumber1 || "—"}</td>
                    <td className="px-4 py-3 text-vf-muted font-mono text-xs">{u.whatsappNumber || "—"}</td>
                    <td className="px-4 py-3">{ROLE_TAG(u.roleName)}</td>
                    <td className="px-4 py-3">
                      {!u.isSuspended
                        ? <StatusTag type="green" text="Active" />
                        : <StatusTag type="slate" text="Suspended" />}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setFormModal({ member: u })} className="btn-secondary btn-sm mr-2">
                        Edit
                      </button>
                      <button onClick={() => setConfirmDelete(u)} className="btn-danger btn-sm">
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formModal && (
        <MemberModal
          member={formModal.member}
          roles={roles}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            loadAll();
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmRemoveModal
          member={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
};

const StatTile = ({ label, value, className }) => (
  <div className="card">
    <div className="stat-label">{label}</div>
    <div className={`text-2xl font-semibold leading-none mt-1 ${className}`}>{value}</div>
  </div>
);

function MemberModal({ member, roles, onClose, onSaved }) {
  const isEdit = !!member;
  const [form, setForm] = useState(() => ({
    id: member?.id ?? null,
    firstName: member?.firstName ?? "",
    lastName: member?.lastName ?? "",
    primaryEmailId: member?.primaryEmailId ?? "",
    mobileNumber1CountryCode: member?.mobileNumber1CountryCode ?? "+91",
    mobileNumber1: member?.mobileNumber1 ?? "",
    whatsappNumber: member?.whatsappNumber ?? "",
    roleId: member?.roleId ?? roles[0]?.id ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.firstName.trim() || !form.primaryEmailId.trim() || !form.roleId) {
      setError("First name, email and role are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        // No dedicated tenant-user update endpoint yet (create-only contract).
        const r = await axiosInstance.post(`${baseUrl}/common/user/save-user-role`, form);
        if (r.data?.success === false) throw new Error(r.data?.message || "Failed to update member.");
        toast.success(r.data?.message || "Member updated.");
      } else {
        // Admin create → backend generates + emails a temporary password.
        const r = await axiosInstance.post(`${baseUrl}/common/tenant/users/create`, {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          primaryEmailId: form.primaryEmailId.trim(),
          mobileNumber1: form.mobileNumber1.trim(),
          mobileNumber1CountryCode: form.mobileNumber1CountryCode.trim(),
          roleId: Number(form.roleId),
        });
        if (r.data?.success === false) throw new Error(r.data?.message || "Failed to create member.");
        toast.success(r.data?.message || "User created. A temporary password has been emailed.");
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to save member.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-vf-base">
            {isEdit ? "Edit member" : "Add team member"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-vf-muted hover:text-vf-base text-xl leading-none"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="modal-body space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *">
              <input className="input" value={form.firstName} onChange={(e) => u("firstName", e.target.value)} autoFocus />
            </Field>
            <Field label="Last name">
              <input className="input" value={form.lastName} onChange={(e) => u("lastName", e.target.value)} />
            </Field>
          </div>
          <Field label="Email *">
            <input
              className="input" type="email" placeholder="member@example.com"
              value={form.primaryEmailId} onChange={(e) => u("primaryEmailId", e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobile number">
              <div className="flex gap-2">
                <input
                  className="input w-16 text-center px-1" aria-label="Country code"
                  value={form.mobileNumber1CountryCode} onChange={(e) => u("mobileNumber1CountryCode", e.target.value)}
                />
                <input
                  className="input flex-1" placeholder="98765 43210"
                  value={form.mobileNumber1} onChange={(e) => u("mobileNumber1", e.target.value)}
                />
              </div>
            </Field>
            <Field label="WhatsApp number">
              <input className="input" placeholder="+91 98765 43210" value={form.whatsappNumber} onChange={(e) => u("whatsappNumber", e.target.value)} />
            </Field>
          </div>
          <Field label="Role *">
            <select className="select" value={form.roleId} onChange={(e) => u("roleId", e.target.value)}>
              <option value="">Select a role…</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{renameRole(r.name)}</option>
              ))}
            </select>
          </Field>

          {error && (
            <div className="alert-danger">{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary btn-sm">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add member"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmRemoveModal({ member, onCancel, onConfirm }) {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ") || "This member";
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-panel !max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-vf-base">Remove team member?</h2>
          <button onClick={onCancel} aria-label="Close" className="text-vf-muted hover:text-vf-base text-xl leading-none">
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">
          <p className="text-sm text-vf-muted leading-relaxed">
            <strong className="text-vf-base">{name}</strong> will lose access to this organization.
            Their data is retained for audit but they will no longer be able to sign in.
          </p>
        </div>
        <div className="modal-footer">
          <button onClick={onCancel} className="btn-secondary btn-sm">Cancel</button>
          <button onClick={onConfirm} className="btn-danger btn-sm">Remove</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export default TeamMembers;
